import React, { createContext, useCallback, useContext, useState } from 'react';

type ComplementItem = {
  id: number | string;
  name?: string;
  order: number;
  rawMaterialId?: number;
  maximumQuantity: number;
  minimumQuantity: number;
  image?: File | string;
  price: number;
  pauseSale?: boolean;
};

type Complement = {
  id?: number | string;
  name?: string;
  order?: number;
  maximumQuantity?: number;
  minimumQuantity?: number;
  isRequired?: boolean;
  items?: ComplementItem[];
  pauseSale?: boolean;
};

type Option = {
  value: number;
  label: string;
};

export type Image = {
  id?: number;
  file?: File | string;
};

export type Grid = {
  id: number | string;
  name: string;
  order: number;
  image?: File | string;
  minimumStock: number;
  currentStock: number;
  price: number;
  discount: number;
  sizeId?: number;
  colorId?: number;
  size?: Option;
  color?: Option;
  code?: string;
  active: boolean;
  emphasis: boolean;
  images: Array<Image | undefined>;
  canChangeCurrentStock: boolean;
};

export type AvailabilityDetails = {
  days: {
    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
  };
  shifts: {
    startTime: number;
    endTime: number;
  }[];
};

type Product = {
  id?: number;
  image?: File | string;
  name?: string;
  gtin?: string;
  groupId?: number;
  observation?: string;
  complements?: Complement[];
  grids: Grid[];
  width?: number;
  height?: number;
  length?: number;
  weight?: number;
  isAwaysAvailability?: boolean;
  unitOfMeasurementId?: number;
  details: AvailabilityDetails;
};

type GridProductContextData = Product & {
  sizesOptions: Option[];
  groupsOptions: Option[];
  colorsOptions: Option[];
  isDrawerOpened?: boolean;
  complementsDrawerTab: 0 | 1;
  currentTab: 0 | 1 | 2 | 3;
  unitsOfMeasurementOptions: Option[];
  isComplementsDrawerOpened?: boolean;
  handleAddGrids(gridsToGenerate: { [colorId: number]: number[] }): void;
  handleAddShift(): void;
  handleOpenDrawer(): void;
  handleCloseDrawer(): void;
  handleResetProduct(): void;
  handleEditGrid(grid: Grid): void;
  handleChangeTab(tab: number): void;
  handleOpenComplementsDrawer(): void;
  handleCloseComplementsDrawer(): void;
  handleToggleCanChangeCurrentStock(gridId: string | number): void;
  handleChangeFeaturedGridProduct(grid: Grid): void;
  handleRemoveShift(shiftIndex: number): void;
  handleChangeSizesOptions(options: Option[]): void;
  handleDeleteGrid(gridId: number | string): void;
  handleAddComplement(complement: Complement): void;
  handleEditComplement(complement: Complement): void;
  handleChangeComplementsDrawerTab(tab: 0 | 1): void;
  handleChangeGroupsOptions(options: Option[]): void;
  handleChangeColorsOptions(options: Option[]): void;
  handleDeleteComplement(complement: Complement): void;
  handleChangeProduct(partialProduct: Partial<Product>): void;
  handleChangeUnitsOfMeasurementOptions(options: Option[]): void;
  handleChangeAvailabilityDays(days: AvailabilityDetails['days']): void;
  handleChangeGridImage(
    gridId: number | string,
    imageIndexToChange: number,
    imageToChange?: File,
  ): void;
  handleChangeShift(
    fieldName: keyof AvailabilityDetails['shifts'][0],
    value: number,
    shiftIndex: number,
  ): void;
};

const GridProductContext = createContext<GridProductContextData>(
  {} as GridProductContextData,
);

const INITIAL_PRODUCT_DATA = {
  unitOfMeasurementId: 6,
  isAwaysAvailability: true,
  grids: [],
  display: false,
  details: {
    days: {
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
    },
    shifts: [],
  },
};

const GridProductProvider: React.FC = ({ children }) => {
  const [currentTab, setCurrentTab] = useState<0 | 1 | 2 | 3>(0);
  const [complementsDrawerTab, setComplementsDrawerTab] = useState<0 | 1>(0);
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean | undefined>(
    undefined,
  );
  const [isComplementsDrawerOpened, setIsComplementsDrawerOpened] = useState<
    boolean | undefined
  >(undefined);
  const [product, setProduct] = useState<Product>(INITIAL_PRODUCT_DATA);
  const [unitsOfMeasurementOptions, setUnitsOfMeasurementOptions] = useState<
    Option[]
  >([]);
  const [groupsOptions, setGroupsOptions] = useState<Option[]>([]);
  const [sizesOptions, setSizesOptions] = useState<Option[]>([]);
  const [colorsOptions, setColorsOptions] = useState<Option[]>([]);

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpened(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpened(false);
  }, []);

  const handleOpenComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(true);
  }, []);

  const handleCloseComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(false);
  }, []);

  const handleChangeGroupsOptions = useCallback((options: Option[]) => {
    setGroupsOptions(options);
  }, []);

  const handleChangeSizesOptions = useCallback((options: Option[]) => {
    setSizesOptions(options);
  }, []);

  const handleChangeColorsOptions = useCallback((options: Option[]) => {
    setColorsOptions(options);
  }, []);

  const handleChangeUnitsOfMeasurementOptions = useCallback(
    (options: Option[]) => {
      setUnitsOfMeasurementOptions(options);
    },
    [],
  );

  const handleAddComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: [...(oldState?.complements || []), complement],
    }));
  }, []);

  const handleAddGrids = useCallback(
    (gridsToGenerate: { [colorId: number]: number[] }) => {
      const gridsToBeAdded: Grid[] = [];

      Object.keys(gridsToGenerate).forEach((colorId) => {
        const grids = gridsToGenerate[Number(colorId)].map((sizeId) => {
          const size = sizesOptions.find(
            (sizeOption) => sizeOption.value === sizeId,
          );

          const color = colorsOptions.find(
            (colorOption) => colorOption.value === Number(colorId),
          );

          return {
            id: String(Math.random()),
            active: true,
            emphasis: false,
            currentStock: 0,
            minimumStock: 0,
            discount: 0,
            price: 0,
            image: undefined,
            order: 0,
            code: '',
            name: `${size?.label || ''} - ${color?.label || ''}`,
            images: Array(3).fill(undefined),
            canChangeCurrentStock: false,
            sizeId,
            colorId: Number(colorId),
            size,
            color,
          };
        });

        gridsToBeAdded.push(...grids);
      });

      setProduct((oldState) => {
        const grids = [
          ...oldState.grids.filter((grid) => typeof grid.id === 'number'),
          ...gridsToBeAdded,
        ];

        return {
          ...oldState,
          grids: grids.map((grid, gridIndex) => ({
            ...grid,
            order: gridIndex + 1,
          })),
        };
      });
    },
    [colorsOptions, sizesOptions],
  );

  const handleChangeGridImage = useCallback(
    (
      gridId: number | string,
      imageIndexToChange: number,
      imageToChange?: File,
    ) => {
      setProduct((oldState) => ({
        ...oldState,
        grids: oldState.grids.map((oldGrid) => {
          if (oldGrid.id === gridId) {
            return {
              ...oldGrid,
              images: oldGrid.images.map((image, imageIndex) => {
                if (imageIndex === imageIndexToChange) {
                  if (!imageToChange && !image?.id) {
                    return undefined;
                  }

                  return {
                    ...image,
                    file: imageToChange,
                  };
                }

                return image;
              }),
            };
          }

          return oldGrid;
        }),
      }));
    },
    [],
  );

  const handleDeleteComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).filter(
        (oldComplement) => oldComplement.id !== complement.id,
      ),
    }));
  }, []);

  const handleDeleteGrid = useCallback((gridId: number | string) => {
    setProduct((oldState) => ({
      ...oldState,
      grids: oldState.grids.filter((oldGrid) => oldGrid.id !== gridId),
    }));
  }, []);

  const handleEditComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).map((oldComplement) => {
        if (oldComplement.id === complement.id) {
          return complement;
        }

        return oldComplement;
      }),
    }));
  }, []);

  const handleEditGrid = useCallback((grid: Grid) => {
    setProduct((oldState) => ({
      ...oldState,
      grids: oldState.grids.map((oldGrid) => {
        if (oldGrid.id === grid.id) {
          return grid;
        }

        return oldGrid;
      }),
    }));
  }, []);

  const handleToggleCanChangeCurrentStock = useCallback(
    (gridId: string | number) => {
      setProduct((oldState) => ({
        ...oldState,
        grids: oldState.grids.map((oldGrid) => {
          if (oldGrid.id === gridId) {
            return {
              ...oldGrid,
              canChangeCurrentStock: !oldGrid.canChangeCurrentStock,
            };
          }

          return oldGrid;
        }),
      }));
    },
    [],
  );

  const handleChangeFeaturedGridProduct = useCallback((grid: Grid) => {
    setProduct((oldState) => {
      const newGrids = oldState.grids.map((item) => {
        if (grid.emphasis === false && item.id === grid.id) {
          return { ...item, emphasis: true };
        }
        return { ...item, emphasis: false };
      });

      return {
        ...oldState,
        grids: newGrids,
      };
    });
  }, []);

  const handleChangeProduct = useCallback(
    (partialProduct: Partial<Product>) => {
      setProduct((oldState) => ({ ...oldState, ...partialProduct }));
    },
    [],
  );

  const handleResetProduct = useCallback(() => {
    setProduct(INITIAL_PRODUCT_DATA);
  }, []);

  const handleAddShift = useCallback(() => {
    setProduct((oldState) => ({
      ...oldState,
      details: {
        days: oldState.details.days,
        shifts: [...oldState.details.shifts, { startTime: 1, endTime: 23 }],
      },
    }));
  }, []);

  const handleChangeShift = useCallback(
    (
      fieldName: keyof AvailabilityDetails['shifts'][0],
      value: number,
      shiftIndex: number,
    ) => {
      setProduct((oldState) => ({
        ...oldState,
        details: {
          days: oldState.details.days,
          shifts: oldState.details.shifts.map((shift, index) => {
            if (shiftIndex === index) {
              return {
                ...shift,
                [fieldName]: value,
              };
            }

            return shift;
          }),
        },
      }));
    },
    [],
  );

  const handleRemoveShift = useCallback((shiftIndex: number) => {
    setProduct((oldState) => ({
      ...oldState,
      details: {
        days: oldState.details.days,
        shifts: oldState.details.shifts.filter(
          (_shift, index) => shiftIndex !== index,
        ),
      },
    }));
  }, []);

  const handleChangeTab = useCallback((tab: 0 | 1 | 2 | 3) => {
    setCurrentTab(tab);
  }, []);

  const handleChangeComplementsDrawerTab = useCallback((tab: 0 | 1) => {
    setComplementsDrawerTab(tab);
  }, []);

  const handleChangeAvailabilityDays = useCallback(
    (days: AvailabilityDetails['days']) => {
      setProduct((oldState) => ({
        ...oldState,
        details: { ...oldState.details, days },
      }));
    },
    [],
  );

  return (
    <GridProductContext.Provider
      value={{
        currentTab,
        sizesOptions,
        colorsOptions,
        groupsOptions,
        isDrawerOpened,
        complementsDrawerTab,
        isComplementsDrawerOpened,
        unitsOfMeasurementOptions,
        handleAddGrids,
        handleEditGrid,
        handleAddShift,
        handleChangeTab,
        handleDeleteGrid,
        handleOpenDrawer,
        handleChangeShift,
        handleCloseDrawer,
        handleRemoveShift,
        handleResetProduct,
        handleChangeProduct,
        handleAddComplement,
        handleEditComplement,
        handleChangeGridImage,
        handleDeleteComplement,
        handleChangeGroupsOptions,
        handleChangeSizesOptions,
        handleChangeColorsOptions,
        handleOpenComplementsDrawer,
        handleChangeAvailabilityDays,
        handleCloseComplementsDrawer,
        handleChangeComplementsDrawerTab,
        handleToggleCanChangeCurrentStock,
        handleChangeFeaturedGridProduct,
        handleChangeUnitsOfMeasurementOptions,
        ...product,
      }}
    >
      {children}
    </GridProductContext.Provider>
  );
};

function useGridProduct(): GridProductContextData {
  const context = useContext(GridProductContext);

  if (!context)
    throw new Error('useGridProduct must be used within GridProductProvider');

  return context;
}

export { GridProductProvider, useGridProduct };
